import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Clock, User as UserIcon, CalendarCheck, Calendar, KeyRound } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

export default function StaffSetup() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [anytimeOk, setAnytimeOk] = useState(false);
  const [desiredDays, setDesiredDays] = useState("");
  const [maxDays, setMaxDays] = useState("");
  const [preferredWds, setPreferredWds] = useState([]);
  const [saving, setSaving] = useState(false);

  // パスワード変更
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNext, setPwNext] = useState("");
  const [pwNext2, setPwNext2] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    if (!pwCurrent || !pwNext || !pwNext2) {
      toast.error("全ての項目を入力してください");
      return;
    }
    if (pwNext !== pwNext2) {
      toast.error("新しいパスワードが一致しません");
      return;
    }
    if (pwNext.length < 6) {
      toast.error("新しいパスワードは6文字以上で入力してください");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNext });
      toast.success("パスワードを変更しました");
      setPwCurrent("");
      setPwNext("");
      setPwNext2("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || user.name || "");
      setStartTime(user.default_start_time || "");
      setEndTime(user.default_end_time || "");
      setAnytimeOk(!!user.default_anytime_ok);
      setDesiredDays(user.monthly_desired_days?.toString() || "");
      setMaxDays(user.monthly_max_days?.toString() || "");
      setPreferredWds(user.preferred_weekdays || []);
    }
  }, [user]);

  const toggleWd = (wd) => {
    setPreferredWds((prev) => prev.includes(wd) ? prev.filter((x) => x !== wd) : [...prev, wd].sort());
  };

  const save = async () => {
    if (!displayName.trim()) {
      toast.error("表示名を入力してください");
      return;
    }
    if (!anytimeOk && (!startTime || !endTime)) {
      toast.error("デフォルト時間を入力するか「何時でもOK」を選んでください");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me/profile", {
        display_name: displayName.trim(),
        default_start_time: anytimeOk ? null : startTime,
        default_end_time: anytimeOk ? null : endTime,
        default_anytime_ok: anytimeOk,
        monthly_desired_days: desiredDays ? parseInt(desiredDays, 10) : null,
        monthly_max_days: maxDays ? parseInt(maxDays, 10) : null,
        preferred_weekdays: preferredWds,
      });
      setUser(data);
      localStorage.setItem("shift_user", JSON.stringify(data));
      toast.success("設定を保存しました");
      navigate("/staff", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const isInitial = !user?.profile_completed;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8" data-testid="staff-setup-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-blue-600 mb-3">
            <UserIcon className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {isInitial ? "はじめに設定してください" : "プロフィール設定"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            ここで登録した情報は、毎月の希望提出を早くするために使われます
          </p>
        </div>

        <Card className="p-5 bg-white border border-slate-200 space-y-5">
          <div>
            <Label className="text-sm font-semibold text-slate-700">表示名 (ニックネーム可)</Label>
            <Input
              data-testid="setup-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="例: 佐藤さん / さとう"
              className="mt-2 h-12 text-base"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              デフォルト時間
            </Label>
            <p className="text-xs text-slate-500 mt-1">よく入る時間を登録しておきます</p>
            <button
              type="button"
              data-testid="setup-anytime-toggle"
              onClick={() => setAnytimeOk(!anytimeOk)}
              className={`w-full mt-3 px-4 py-3 rounded-md border-2 text-sm font-semibold transition-colors ${
                anytimeOk
                  ? "bg-blue-50 border-blue-500 text-blue-700"
                  : "bg-white border-slate-200 text-slate-600"
              }`}
            >
              {anytimeOk ? "✓ 何時でもOK" : "何時でもOK にする"}
            </button>
            {!anytimeOk && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs text-slate-500">開始時間</Label>
                  <Input data-testid="setup-start-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1 h-12 text-base" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">終了時間</Label>
                  <Input data-testid="setup-end-time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1 h-12 text-base" />
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <CalendarCheck className="w-4 h-4" />
              月間 希望出勤日数 / 上限日数
            </Label>
            <p className="text-xs text-slate-500 mt-1">希望日数は「これくらい入りたい」、上限日数は「これ以上は入れない」目安です</p>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs text-slate-500">希望日数 / 月</Label>
                <Input data-testid="setup-desired-days" type="number" min={0} max={31} value={desiredDays} onChange={(e) => setDesiredDays(e.target.value)} className="mt-1 h-12 text-base" placeholder="例: 12" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">上限日数 / 月</Label>
                <Input data-testid="setup-max-days" type="number" min={0} max={31} value={maxDays} onChange={(e) => setMaxDays(e.target.value)} className="mt-1 h-12 text-base" placeholder="例: 14" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              基本的に出られる曜日 (任意)
            </Label>
            <p className="text-xs text-slate-500 mt-1">担当者が「追加で相談する候補」を選ぶ時の参考になります</p>
            <div className="grid grid-cols-7 gap-1.5 mt-3">
              {WEEKDAYS.map((w, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`setup-wd-${i}`}
                  onClick={() => toggleWd(i)}
                  className={`h-12 rounded-md border-2 text-sm font-semibold ${
                    preferredWds.includes(i)
                      ? "bg-blue-50 border-blue-500 text-blue-700"
                      : i === 0 ? "bg-white border-slate-200 text-red-500"
                      : i === 6 ? "bg-white border-slate-200 text-blue-500"
                      : "bg-white border-slate-200 text-slate-600"
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>

          <Button
            data-testid="setup-save-button"
            onClick={save}
            disabled={saving}
            className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base font-semibold"
          >
            {saving ? "保存中..." : isInitial ? "設定して始める" : "保存"}
          </Button>
        </Card>

        {!isInitial && (
          <Card className="p-5 bg-white border border-slate-200 mt-4 space-y-4" data-testid="staff-password-section">
            <div className="flex items-start gap-2">
              <KeyRound className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h2 className="text-base font-bold text-slate-900 leading-tight">パスワード変更</h2>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  忘れてしまった場合は、店長さんに新しいパスワードの発行を依頼してください。
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">現在のパスワード</Label>
                <Input
                  type="password"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  className="mt-1 h-11 text-base"
                  autoComplete="current-password"
                  data-testid="staff-pw-current"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">新しいパスワード（6文字以上）</Label>
                <Input
                  type="password"
                  value={pwNext}
                  onChange={(e) => setPwNext(e.target.value)}
                  className="mt-1 h-11 text-base"
                  autoComplete="new-password"
                  data-testid="staff-pw-next"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">新しいパスワード（確認）</Label>
                <Input
                  type="password"
                  value={pwNext2}
                  onChange={(e) => setPwNext2(e.target.value)}
                  className="mt-1 h-11 text-base"
                  autoComplete="new-password"
                  data-testid="staff-pw-next2"
                />
              </div>
              <Button
                onClick={changePassword}
                disabled={pwSaving}
                data-testid="staff-pw-submit"
                className="w-full bg-slate-800 hover:bg-slate-900 h-11 text-sm font-semibold"
              >
                {pwSaving ? "変更中..." : "パスワードを変更"}
              </Button>
            </div>
          </Card>
        )}

        {!isInitial && (
          <div className="text-center mt-4">
            <button onClick={() => navigate("/staff")} className="text-sm text-slate-500 hover:text-slate-700" data-testid="setup-back-link">
              ホームへ戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
